import {ActionReducerMapBuilder, createSlice} from '@reduxjs/toolkit'
import {
  ACTIVITY_BY_TIME,
  AnalyticsActivityByTimeSliceState,
  AnalyticsTopCategoriesSliceState,
  ProductivitySearchInputState,
  RESOURCE_TYPE_SINGULAR,
  TopCategoriesTimeStampState,
} from 'interfaces'
import {
  resetProductivity,
  resetActivityByTime,
  setProductivityTitle,
  setProductivitySearchInputs,
  resetProductivitySearchInputs,
  setTopCategoriesTimeStamp,
  setActivityByTimeFormat,
} from './analyticsProductivityAction'
import {topCategories} from './constants'

const initialTopCategories: AnalyticsTopCategoriesSliceState = {
  title: topCategories,
}

const analyticsTopCategoriesSlice = createSlice({
  name: 'analyticsTopCategories',
  initialState: initialTopCategories,
  reducers: {},
  extraReducers: (
    builder: ActionReducerMapBuilder<AnalyticsTopCategoriesSliceState>,
  ) =>
    builder
      .addCase(setProductivityTitle, (state, action) => ({
        ...state,
        title: action?.payload || topCategories,
      }))
      .addCase(resetProductivity, state => ({
        ...state,
        ...initialTopCategories,
      }))
      .addDefaultCase(state => state),
})

export const {
  reducer: analyticsTopCategoriesReducer,
  name: analyticsTopCategoriesReducerName,
} = analyticsTopCategoriesSlice

export type AnalyticTopCategoriesReducerState = ReturnType<
  typeof analyticsTopCategoriesReducer
>

const initialActivityByTimeState: AnalyticsActivityByTimeSliceState = {
  activityByTimeFormat: ACTIVITY_BY_TIME.Week,
}

const analyticActivityByTimeSlice = createSlice({
  name: 'analyticActivityByTime',
  initialState: initialActivityByTimeState,
  reducers: {},
  extraReducers: (
    builder: ActionReducerMapBuilder<AnalyticsActivityByTimeSliceState>,
  ) =>
    builder
      .addCase(setActivityByTimeFormat, (state, action) => ({
        ...state,
        activityByTimeFormat: action?.payload,
      }))
      .addCase(resetActivityByTime, state => ({
        ...state,
        ...initialActivityByTimeState,
      }))
      .addDefaultCase(state => state),
})

export const {
  reducer: analyticsActivityByTimeReducer,
  name: analyticsActivityByTimeReducerName,
} = analyticActivityByTimeSlice

export type AnalyticActivityByTimeReducerState = ReturnType<
  typeof analyticsActivityByTimeReducer
>

// productivity search reducer
const initialProductivitySearchInputState = {
  searchTerm: '',
  searchDisplayValue: '',
  searchType: '' as RESOURCE_TYPE_SINGULAR,
  name: '',
}

const productivitySearchInputsSlice = createSlice({
  name: 'productivitySearchInputs',
  initialState: initialProductivitySearchInputState,
  reducers: {},
  extraReducers: (
    builder: ActionReducerMapBuilder<ProductivitySearchInputState>,
  ) =>
    builder
      .addCase(setProductivitySearchInputs, (state, action) => ({
        ...state,
        searchTerm: action?.payload?.searchTerm,
        searchDisplayValue: action?.payload?.searchDisplayValue,
        searchType: action?.payload?.searchType,
        name: action?.payload?.name,
      }))
      .addCase(resetProductivitySearchInputs, () => ({
        ...initialProductivitySearchInputState,
      }))
      .addDefaultCase(state => state),
})

export const {
  reducer: productivitySearchInputsReducer,
  name: productivitySearchInputsReducerName,
} = productivitySearchInputsSlice

export type ProductivitySearchInputsReducerState = ReturnType<
  typeof productivitySearchInputsReducer
>

// top categories timestamp
const initialTopCategoriesTimeStampState = {
  timeStamp: 0,
}

const topCategoriesTimeStampSlice = createSlice({
  name: 'topCategoriesTimeStampSlice',
  initialState: initialTopCategoriesTimeStampState,
  reducers: {},
  extraReducers: (
    builder: ActionReducerMapBuilder<TopCategoriesTimeStampState>,
  ) =>
    builder
      .addCase(setTopCategoriesTimeStamp, (state, action) => ({
        ...state,
        timeStamp: action?.payload || 0,
      }))
      .addDefaultCase(state => state),
})

export const {
  reducer: topCategoriesTimeStampReducer,
  name: topCategoriesTimeStampReducerName,
} = topCategoriesTimeStampSlice

export type TopCategoriesTimeStampReducerState = ReturnType<
  typeof topCategoriesTimeStampReducer
>

export type TAnalyticsProductivityActions =
  // productivity
  | ReturnType<typeof setProductivityTitle>
  | ReturnType<typeof resetProductivity>
  // activity by time
  | ReturnType<typeof setActivityByTimeFormat>
  | ReturnType<typeof resetActivityByTime>
  // search
  | ReturnType<typeof setProductivitySearchInputs>
  | ReturnType<typeof resetProductivitySearchInputs>
  | ReturnType<typeof setTopCategoriesTimeStamp>

export const analyticsProductivityActions = {
  // productivity
  setProductivityTitle,
  resetProductivity,
  // activity by time
  setActivityByTimeFormat,
  resetActivityByTime,
  // search
  setProductivitySearchInputs,
  resetProductivitySearchInputs,
  setTopCategoriesTimeStamp,
}
